import { useQuery } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import { Badge, Button, Field, Input, Select, StatusPill, Textarea } from '@bb/ui';
import { useRef, type ReactNode } from 'react';
import { ApiError, type AdminApi, type TemplateVariable, type WebhookEndpointSummary, type WebhookParam } from '../../../api/client.ts';
import { PayloadPreviewPanel, type PreviewSample } from '../shared/PayloadPreviewPanel.tsx';
import { PersonalizationButton } from '../shared/PersonalizationButton.tsx';
import { PARAM_TYPE_HINTS, paramVariables, readWebhookContent, switchEndpoint, toSummary, webhookContentIssues, writeWebhookContent, type WebhookContent } from './webhookContent.ts';

export interface WebhookComposerProps {
  environmentId: string;
  /** The channel content as stored (`{ endpointId, params, bodyTemplate? }`). */
  value: unknown;
  onChange: (next: Record<string, unknown>) => void;
  /** A campaign variant's composer: the campaign's trigger data is offered and previewed. */
  campaignId?: string;
  /** Trigger data samples of the preview (a Canvas step: its context). */
  samples?: readonly PreviewSample[];
  readOnly?: boolean;
}

/** Endpoints of the environment as a composer sees them (summaries; integrators get full views with the same fields). */
export function useWebhookEndpoints(environmentId: string) {
  const { api } = useRouter().options.context as { api: AdminApi };
  return useQuery({ queryKey: ['webhook-endpoints', environmentId], queryFn: () => api.listWebhookEndpoints(environmentId), staleTime: 30_000 });
}

/** One Liquid text box in a field, with its personalization button beside it (as the push composer's text fields). */
function LiquidField(props: {
  environmentId: string;
  campaignId?: string | undefined;
  label: ReactNode;
  name: string;
  hint?: string | undefined;
  error?: string | undefined;
  value: string;
  multiline: boolean;
  readOnly: boolean;
  extraVariables?: readonly TemplateVariable[];
  onChange: (next: string) => void;
}) {
  const ref = useRef<HTMLInputElement & HTMLTextAreaElement>(null);
  const { value, readOnly, onChange } = props;
  return (
    <div className="push-text-field">
      <div className="push-text-field__row">
        <Field label={props.label} hint={props.hint} error={props.error}>
          {props.multiline ? (
            <Textarea ref={ref} rows={props.extraVariables ? 6 : 3} spellCheck={false} value={value} disabled={readOnly} onChange={(e) => onChange(e.target.value)} />
          ) : (
            <Input ref={ref} spellCheck={false} value={value} disabled={readOnly} onChange={(e) => onChange(e.target.value)} />
          )}
        </Field>
        <PersonalizationButton
          environmentId={props.environmentId}
          {...(props.campaignId ? { campaignId: props.campaignId } : {})}
          target={ref}
          value={value}
          onChange={onChange}
          label={`Personalize ${props.name}`}
          disabled={readOnly}
          {...(props.extraVariables ? { extraVariables: props.extraVariables } : {})}
        />
      </div>
    </div>
  );
}

function ParamField({ param, ...rest }: { param: WebhookParam; environmentId: string; campaignId?: string | undefined; value: string; error?: string | undefined; readOnly: boolean; onChange: (next: string) => void }) {
  const label = (
    <>
      {param.name} <Badge>{param.type}</Badge> {param.required ? <Badge tone="warning">required</Badge> : <span className="hint">optional</span>}
    </>
  );
  return (
    <div role="group" aria-label={`Param ${param.name}`}>
      <LiquidField {...rest} label={label} name={param.name} hint={[param.description, PARAM_TYPE_HINTS[param.type]].filter(Boolean).join(' ')} multiline={param.type === 'json'} />
    </div>
  );
}

const endpointLabel = (e: WebhookEndpointSummary) => `${e.name} — ${e.method} ${e.origin}${e.enabled ? '' : ' (disabled)'}`;

/**
 * Composer of the `webhook` channel (BB-142) for a campaign variant or a Canvas Message step: pick a registered
 * endpoint, fill each of its typed params with a Liquid expression, optionally replace the default JSON body with a
 * template that reads `{{ params.<name> }}`, and preview the exact request for a user. The URL, headers and secrets
 * belong to the endpoint (Settings → Webhooks), never to the message.
 */
export function WebhookComposer({ environmentId, value, onChange, campaignId, samples, readOnly = false }: WebhookComposerProps) {
  const endpoints = useWebhookEndpoints(environmentId);
  const c = readWebhookContent(value);
  const list = (endpoints.data ?? []).map(toSummary);
  const endpoint = list.find((e) => e.id === c.endpointId);
  const issues = webhookContentIssues(c, endpoint, Boolean(endpoints.data));
  const set = (next: WebhookContent) => onChange(writeWebhookContent(value, next));
  const content = c.endpointId ? writeWebhookContent(value, c) : undefined;
  const loadError = endpoints.error instanceof ApiError ? endpoints.error.message : 'The webhook endpoints could not be loaded.';

  return (
    <section aria-label="Webhook" className="step-panel__channel webhook-composer">
      <h4>Webhook</h4>
      {endpoints.isError && <p role="alert">{loadError}</p>}
      {endpoints.data && list.length === 0 && <p className="notice notice--warning">No webhook endpoints yet: an integrator registers them in Settings → Webhooks (URL, headers, secrets and params).</p>}
      <Field label="Endpoint" error={issues.endpoint} hint="Messages call only endpoints registered for this environment.">
        <Select
          placeholder={endpoints.isPending ? 'Loading…' : 'Select…'}
          options={[...list.map((e) => ({ value: e.id, label: endpointLabel(e) })), ...(c.endpointId && !endpoint ? [{ value: c.endpointId, label: 'Archived endpoint' }] : [])]}
          value={c.endpointId}
          disabled={readOnly || !endpoints.data}
          onChange={(e) => set(switchEndpoint(c, list.find((x) => x.id === e.target.value)))}
        />
      </Field>
      {endpoint && (
        <p className="hint" aria-label="Endpoint details">
          {endpoint.method} {endpoint.origin} · {endpoint.signed ? 'Signed (X-BB-Signature)' : 'Not signed'} · body {endpoint.contentType}
          {endpoint.description ? ` · ${endpoint.description}` : ''} {!endpoint.enabled && <StatusPill tone="warning">Disabled: sends stop until it is enabled</StatusPill>}
        </p>
      )}
      {endpoint && (
        <fieldset className="stack-form">
          <legend>Params</legend>
          {endpoint.params.length === 0 && <p className="hint">This endpoint takes no params.</p>}
          {endpoint.params.map((p) => (
            <ParamField key={p.name} param={p} environmentId={environmentId} campaignId={campaignId} value={c.params[p.name] ?? ''} error={issues.params[p.name]} readOnly={readOnly} onChange={(v) => set({ ...c, params: { ...c.params, [p.name]: v } })} />
          ))}
          {issues.unknownParams.length > 0 && (
            <div role="alert">
              <p className="bb-field__error">The endpoint does not declare {issues.unknownParams.join(', ')}: the send would fail (param_unknown).</p>
              {!readOnly && (
                <Button size="sm" onClick={() => set(switchEndpoint(c, endpoint))}>
                  Remove undeclared params
                </Button>
              )}
            </div>
          )}
        </fieldset>
      )}
      {endpoint && (
        <LiquidField
          environmentId={environmentId}
          campaignId={campaignId}
          label="Body template (optional)"
          name="body template"
          error={issues.bodyTemplate}
          hint={`Empty: the body is the params as a JSON object. A template is sent as ${endpoint.contentType} and reads the params as {{ params.<name> }}${endpoint.contentType === 'application/json' ? '; print strings with | json' : ''}.`}
          value={c.bodyTemplate ?? ''}
          multiline
          readOnly={readOnly}
          extraVariables={paramVariables(endpoint)}
          onChange={(v) => set({ ...c, bodyTemplate: v })}
        />
      )}
      <PayloadPreviewPanel environmentId={environmentId} channels={['webhook']} {...(content ? { content } : {})} {...(campaignId && content ? { campaignId } : {})} {...(samples ? { samples } : {})} title="Preview request" />
    </section>
  );
}
